import { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import { Button, Input, InputNumber, Modal, Space } from 'antd';

import { uploadImageApiCall } from '../../Api/config';
import { BASE_IMAGE_URL } from '../../env';
import { toastError, toastInfo, toastSuccess } from '../../service/Toast';

import 'react-quill/dist/quill.snow.css';

const BlockEmbed = Quill.import('blots/block/embed') as any;

class TableEmbed extends BlockEmbed {
  static create(value: string) {
    const node = super.create();
    node.setAttribute('contenteditable', 'false');
    node.setAttribute('data-table-embed', 'true');
    node.setAttribute('title', 'Double-click to edit table');
    node.innerHTML = typeof value === 'string' ? value : '';
    return node;
  }

  static value(node: HTMLElement) {
    return node.innerHTML;
  }
}

TableEmbed.blotName = 'tableEmbed';
TableEmbed.tagName = 'div';
TableEmbed.className = 'ql-table-embed';

if (!(Quill as any).__tableEmbedRegistered) {
  Quill.register(TableEmbed);
  (Quill as any).__tableEmbedRegistered = true;
}

function toPublicImageUrl(path?: string) {
  if (!path) return '';
  if (
    path.startsWith('http://') ||
    path.startsWith('https://') ||
    path.startsWith('data:') ||
    path.startsWith('blob:')
  ) {
    return path;
  }
  const base = (BASE_IMAGE_URL || '').replace(/\/+$/, '');
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalized}`;
}

function createEmptyGrid(rows: number, cols: number): string[][] {
  return Array.from({ length: rows }, () => Array.from({ length: cols }, () => ''));
}

function parseTableHtml(html: string): string[][] {
  try {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    const table = wrapper.querySelector('table');
    if (!table) return createEmptyGrid(3, 3);

    const rows = Array.from(table.querySelectorAll('tr'));
    if (!rows.length) return createEmptyGrid(3, 3);

    return rows.map(row =>
      Array.from(row.querySelectorAll('th,td')).map(cell => (cell.textContent || '').trim())
    );
  } catch {
    return createEmptyGrid(3, 3);
  }
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildTableHtml(grid: string[][]): string {
  if (!grid.length) return '';

  const header = grid[0] || [];
  const bodyRows = grid.slice(1);

  const headerHtml = `<thead><tr>${header
    .map(
      cell =>
        `<th style="border:1px solid #cbd5e1;padding:8px;background:#f1f5f9;text-align:left;">${escapeHtml(
          cell || ' '
        )}</th>`
    )
    .join('')}</tr></thead>`;

  const bodyHtml = `<tbody>${bodyRows
    .map(
      row =>
        `<tr>${row
          .map(
            cell =>
              `<td style="border:1px solid #cbd5e1;padding:8px;">${escapeHtml(cell || ' ')}</td>`
          )
          .join('')}</tr>`
    )
    .join('')}</tbody>`;

  return `<table style="width:100%;border-collapse:collapse;margin:12px 0;">${headerHtml}${bodyHtml}</table>`;
}

interface QuillEditorFieldProps {
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
}

export default function QuillEditorField({ value, onChange, className }: QuillEditorFieldProps) {
  const [editorValue, setEditorValue] = useState(value || '');
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [tableGrid, setTableGrid] = useState<string[][]>(() => createEmptyGrid(3, 3));
  const [editingTableIndex, setEditingTableIndex] = useState<number | null>(null);

  const quillRef = useRef<ReactQuill | null>(null);
  const openNewTableModalRef = useRef<() => void>(() => undefined);
  const openEditTableModalRef = useRef<(index: number, html: string) => void>(() => undefined);

  useEffect(() => {
    setEditorValue(value || '');
  }, [value]);

  const openNewTableModal = () => {
    setEditingTableIndex(null);
    setTableRows(3);
    setTableCols(3);
    setTableGrid(createEmptyGrid(3, 3));
    setTableModalOpen(true);
  };

  const openEditTableModal = (index: number, html: string) => {
    const grid = parseTableHtml(html);
    setEditingTableIndex(index);
    setTableRows(grid.length);
    setTableCols(Math.max(...grid.map(row => row.length), 1));
    setTableGrid(grid);
    setTableModalOpen(true);
  };

  openNewTableModalRef.current = openNewTableModal;
  openEditTableModalRef.current = openEditTableModal;

  useEffect(() => {
    let disposed = false;
    let root: HTMLElement | null = null;

    const onDoubleClick = (event: MouseEvent) => {
      const editor = quillRef.current?.getEditor();
      const target = event.target as HTMLElement | null;
      const tableNode = target?.closest?.('.ql-table-embed') as HTMLElement | null;
      if (!tableNode || !editor) return;

      const blot = (Quill as any).find(tableNode);
      if (!blot) return;

      const index = editor.getIndex(blot);
      openEditTableModalRef.current(index, tableNode.innerHTML);
    };

    const timer = window.setTimeout(() => {
      if (disposed) return;
      root = quillRef.current?.getEditor()?.root || null;
      root?.addEventListener('dblclick', onDoubleClick);
    }, 0);

    return () => {
      disposed = true;
      window.clearTimeout(timer);
      root?.removeEventListener('dblclick', onDoubleClick);
    };
  }, []);

  const resizeGrid = (rows: number, cols: number) => {
    setTableRows(rows);
    setTableCols(cols);
    setTableGrid(previous =>
      Array.from({ length: rows }, (_, rowIndex) =>
        Array.from({ length: cols }, (_, colIndex) => previous[rowIndex]?.[colIndex] || '')
      )
    );
  };

  const insertOrUpdateTable = () => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const html = buildTableHtml(tableGrid);
    if (!html) return;

    if (editingTableIndex !== null) {
      editor.deleteText(editingTableIndex, 1, 'user');
      editor.insertEmbed(editingTableIndex, 'tableEmbed', html, 'user');
      editor.insertText(editingTableIndex + 1, '\n', 'user');
      editor.setSelection(editingTableIndex + 2, 0);
    } else {
      const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
      editor.insertEmbed(range.index, 'tableEmbed', html, 'user');
      editor.insertText(range.index + 1, '\n', 'user');
      editor.setSelection(range.index + 2, 0);
    }

    setTableModalOpen(false);
    setEditingTableIndex(null);
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
          [{ align: [] }, { color: [] }, { background: [] }],
          ['blockquote', 'link', 'image'],
          ['table'],
          ['clean'],
        ],
        handlers: {
          image() {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/*');
            input.click();

            input.onchange = async () => {
              const file = input.files?.[0];
              if (!file) return;

              const editor = quillRef.current?.getEditor();
              if (!editor) return;

              const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
              const formData = new FormData();
              formData.append('iconImage', file);

              try {
                toastInfo('Uploading image...');
                const res: any = await uploadImageApiCall('/documitra-content/upload', formData);
                const uploadedPath = res?.data || res?.url || res?.path;
                if (!res?.isSuccess || !uploadedPath) {
                  throw new Error(res?.message || 'Upload failed');
                }

                const imageUrl = toPublicImageUrl(String(uploadedPath));
                editor.insertEmbed(range.index, 'image', imageUrl, 'user');
                editor.setSelection(range.index + 1, 0);
                toastSuccess('Image uploaded');
              } catch (error) {
                console.error(error);
                toastError('Image upload failed. Please try again.');
              }
            };
          },
          table() {
            openNewTableModalRef.current();
          },
        },
      },
      clipboard: { matchVisual: false },
    }),
    []
  );

  const formats = useMemo(
    () => [
      'header',
      'bold',
      'italic',
      'underline',
      'strike',
      'list',
      'bullet',
      'indent',
      'align',
      'color',
      'background',
      'blockquote',
      'link',
      'image',
      'tableEmbed',
    ],
    []
  );

  return (
    <>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        modules={modules}
        formats={formats}
        className={className}
        value={editorValue}
        onChange={content => {
          setEditorValue(content);
          onChange?.(content);
        }}
      />

      <Modal
        title={editingTableIndex !== null ? 'Edit Table' : 'Insert Table'}
        visible={tableModalOpen}
        onCancel={() => {
          setTableModalOpen(false);
          setEditingTableIndex(null);
        }}
        width={720}
        destroyOnClose
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setTableModalOpen(false);
              setEditingTableIndex(null);
            }}
          >
            Cancel
          </Button>,
          <Button key="insert" type="primary" onClick={insertOrUpdateTable}>
            {editingTableIndex !== null ? 'Update Table' : 'Insert Table'}
          </Button>,
        ]}
      >
        <Space style={{ marginBottom: 16 }}>
          <span>Rows</span>
          <InputNumber
            min={1}
            max={12}
            value={tableRows}
            onChange={next => resizeGrid(Number(next || 1), tableCols)}
          />
          <span>Columns</span>
          <InputNumber
            min={1}
            max={8}
            value={tableCols}
            onChange={next => resizeGrid(tableRows, Number(next || 1))}
          />
        </Space>

        <p style={{ marginBottom: 12, color: '#64748b' }}>
          Enter text in each cell. First row is used as the header. Double-click an inserted table
          later to edit it again.
        </p>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {tableGrid.map((row, rowIndex) => (
                <tr key={`row-${rowIndex}`}>
                  {row.map((cell, colIndex) => (
                    <td
                      key={`cell-${rowIndex}-${colIndex}`}
                      style={{ border: '1px solid #d9d9d9', padding: 6, minWidth: 120 }}
                    >
                      <Input
                        value={cell}
                        placeholder={rowIndex === 0 ? `Header ${colIndex + 1}` : 'Cell text'}
                        onChange={event => {
                          const nextValue = event.target.value;
                          setTableGrid(previous =>
                            previous.map((currentRow, rIdx) =>
                              currentRow.map((currentCell, cIdx) =>
                                rIdx === rowIndex && cIdx === colIndex ? nextValue : currentCell
                              )
                            )
                          );
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>
    </>
  );
}
